/**
 * As versões emitidas e a conferência de um arquivo (E138, GUS-21, ADR-0094). Espelha
 * `apps/api/types/emissoes_registradas.py`.
 *
 * - `GET /casos/:id/emissoes`: cada versão emitida com o código (o SHA-256 dos bytes finais), a
 *   etapa, o instante e a origem. O banco recebe só as versões que pode baixar.
 * - `POST /casos/:id/certificacoes/:doc/conferencia`: o arquivo sobe, o servidor calcula o código e
 *   compara com a referência final da versão. O resultado vem sempre com a referência usada.
 */
import axios, { type AxiosInstance } from 'axios';

import { UPLOAD_TIMEOUT_MS } from '@/lib/case_source_documents';

export type EtapaDaEmissao = 'pre_assinatura' | 'final';

export interface ManifestoDaEmissaoData {
  id: string;
  sha256: string;
  selado_em: string;
}

export interface EmissaoData {
  id: string;
  source_document_id: string;
  ccb_number: string | null;
  versao: number;
  etapa: EtapaDaEmissao;
  codigo: string;
  emitido_em: string;
  assinatura: 'interna_registrada' | 'icp_brasil';
  formato: string | null;
  manifestos: ManifestoDaEmissaoData[] | null;
  retroativo: boolean;
}

export type ResultadoDaConferencia = 'corresponde' | 'diverge' | 'sem_referencia';

export interface ReferenciaDaConferenciaData {
  emissao_id: string;
  versao: number;
  etapa: EtapaDaEmissao;
  codigo: string;
  emitido_em: string;
}

export interface ConferenciaDoArquivoData {
  resultado: ResultadoDaConferencia;
  source_document_id: string;
  versao_pedida: number | null;
  codigo_apresentado: string;
  tamanho: number;
  referencia: ReferenciaDaConferenciaData | null;
  conferido_em: string;
}

export type MotivoDaFalhaDaEmissao =
  | 'arquivo_vazio'
  | 'arquivo_grande'
  | 'nao_encontrado'
  | 'indisponivel';

export class EmissoesError extends Error {
  constructor(readonly motivo: MotivoDaFalhaDaEmissao) {
    super(motivo);
    this.name = 'EmissoesError';
  }
}

const HTTP_BAD_REQUEST = 400;
const HTTP_NOT_FOUND = 404;

function motivoDoErro(err: unknown): MotivoDaFalhaDaEmissao {
  if (!axios.isAxiosError(err)) return 'indisponivel';
  const detail = (err.response?.data as { detail?: unknown } | undefined)?.detail;
  if (err.response?.status === HTTP_BAD_REQUEST) {
    if (detail === 'FILE_EMPTY') return 'arquivo_vazio';
    if (detail === 'FILE_TOO_LARGE') return 'arquivo_grande';
  }
  if (err.response?.status === HTTP_NOT_FOUND) return 'nao_encontrado';
  return 'indisponivel';
}

export async function listarEmissoes(
  client: AxiosInstance,
  caseId: string,
): Promise<EmissaoData[]> {
  try {
    const { data } = await client.get<{ emissoes: EmissaoData[] }>(`/casos/${caseId}/emissoes`);
    return data.emissoes;
  } catch (err) {
    throw new EmissoesError(motivoDoErro(err));
  }
}

export async function conferirArquivo(
  client: AxiosInstance,
  caseId: string,
  documentoId: string,
  arquivo: File,
  versao?: number,
): Promise<ConferenciaDoArquivoData> {
  const form = new FormData();
  form.append('arquivo', arquivo);
  try {
    const { data } = await client.post<ConferenciaDoArquivoData>(
      `/casos/${caseId}/certificacoes/${documentoId}/conferencia`,
      form,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: versao === undefined ? undefined : { versao },
        timeout: UPLOAD_TIMEOUT_MS,
      },
    );
    return data;
  } catch (err) {
    throw new EmissoesError(motivoDoErro(err));
  }
}
